import { BtnIconCellRendererComponent } from './components/btn-icon-cell-renderer.component';
import { BtnCellClickRendererComponent } from './components/btn-cell-click-renderer.component';
import { BtnCellEditRendererComponent } from './components/btn-cell-edit-renderer.component';
import { BtnCellRendererComponent } from './components/btn-cell-renderer.component';
import {
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
	AfterViewInit,
	OnDestroy,
	ElementRef,
	HostListener,
	inject,
	ChangeDetectionStrategy,
} from '@angular/core';
import {
	CellValueChangedEvent,
	ColDef,
	GridApi,
	GridOptions,
	GridReadyEvent,
	IRowNode,
	RowNode,
	RowValueChangedEvent,
	RowSelectionOptions,
} from 'ag-grid-community';
import { Action } from './models/action';
import { getAgGridLocale } from './locale';
import { TranslateService } from '@ngx-translate/core';
import { MobileRendererComponent } from './components/mobile-renderer/mobile-renderer.component';
import { AuthService } from 'app/@core/services/auth.service';
import { Router } from '@angular/router';
import { menu } from 'app/menu/menu';
import { universityMenu } from 'app/menu/university';
import { schoolMenu } from 'app/menu/school';
import { frontendMenu } from 'app/menu/frontend';
import { CoreMenu } from '@core/types';
import { BaseComponent } from 'app/custom-class/base.page';
import { TableStateService } from 'app/@core/services/table-state.service';

@Component({
	selector: 'core-table',
	templateUrl: './core-table.component.html',
	styleUrls: ['./core-table.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CoreTableComponent implements OnInit, AfterViewInit, OnDestroy {
	/** Altezza header (px) usata nella stima dell'altezza 'auto' quando headerHeight non è impostato. */
	private static readonly DEFAULT_HEADER_PX = 48;

	private parentPage = inject(BaseComponent, { optional: true, skipSelf: true });

	@Input() tableName: string;
	/** Colonne: quelle dei pulsanti d'azione (modifica, Personalizza, Ripristina...) sono sempre fissate a destra, in tutte le liste */
	@Input() set columnDefs(value: ColDef[]) {
		this._columnDefs = value?.map(col => (this.isActionColumn(col) ? { ...col, pinned: col.pinned ?? 'right', lockPinned: col.lockPinned ?? true } : col));
	}
	get columnDefs(): ColDef[] {
		return this._columnDefs;
	}
	private _columnDefs: ColDef[];

	private isActionColumn(col: ColDef): boolean {
		return (
			col.headerName === '' &&
			[BtnIconCellRendererComponent, BtnCellClickRendererComponent, BtnCellEditRendererComponent, BtnCellRendererComponent].includes(
				col.cellRenderer as any
			)
		);
	}
	@Input() rowData: Array<any>;
	/**
	 * Modalità altezza griglia:
	 * - 'auto' (default): altezza fissa automatica quando le righe superano autoFixedHeightThreshold,
	 *     altrimenti la griglia cresce con il contenuto. Sopra soglia si attiva la virtualizzazione
	 *     (domLayout="normal") cappando l'altezza a ~threshold righe con scroll interno.
	 * - true: sempre altezza fissa (gridHeight) + virtualizzazione.
	 * - false: sempre altezza variabile (domLayout="autoHeight"), nessuna virtualizzazione.
	 */
	@Input() fixedHeight: boolean | 'auto' = 'auto';
	/** In modalità 'auto', numero di righe oltre il quale si passa ad altezza fissa + virtualizzazione. */
	@Input() autoFixedHeightThreshold: number = 30;
	/** Stima px per riga, usata per calcolare l'altezza fissa (di soglia righe) in modalità 'auto'. */
	@Input() rowHeightEstimate: number = 48;
	/** Altezza della griglia quando fixedHeight=true (override esplicito). */
	@Input() gridHeight: string = 'calc(100vh - 22rem)';

	/**
	 * true quando la griglia deve usare altezza fissa + virtualizzazione (domLayout="normal").
	 * - fixedHeight=false → mai;
	 * - fixedHeight=true → sempre;
	 * - 'auto' → solo quando le righe superano la soglia (le liste corte crescono col contenuto).
	 */
	get useFixedHeight(): boolean {
		if (this.fixedHeight === true) {
			return true;
		}
		if (this.fixedHeight === false) {
			return false;
		}
		return (this.rowData?.length ?? 0) > this.autoFixedHeightThreshold;
	}

	/**
	 * domLayout effettivo. In modalità 'auto' commuta in base al numero di righe: "autoHeight" per le
	 * liste corte (si adattano al contenuto, testo a capo, niente scroll), "normal" (virtualizzato) per
	 * quelle lunghe. La commutazione a runtime è resa stabile forzando un relayout in onRowDataUpdatedInternal.
	 */
	get effectiveDomLayout(): 'normal' | 'autoHeight' {
		return this.useFixedHeight ? 'normal' : 'autoHeight';
	}

	/** Altezza applicata alla griglia: solo in modalità fissa (domLayout="normal"); null con autoHeight. */
	get effectiveHeight(): string | null {
		if (!this.useFixedHeight) {
			return null;
		}
		// fixedHeight=true → altezza esplicita (es. relativa al viewport)
		if (this.fixedHeight === true) {
			return this.gridHeight;
		}
		// 'auto' sopra soglia → cappa all'altezza di ~soglia righe (header incluso)
		const headerPx = this.headerHeight ?? CoreTableComponent.DEFAULT_HEADER_PX;
		return `${headerPx + this.autoFixedHeightThreshold * this.rowHeightEstimate}px`;
	}
	@Input() set rowSelection(value: RowSelectionOptions | 'multiRow' | 'singleRow' | 'none') {
		this._rawRowSelection = value;
		this.updateRowSelectionOptions();
	}
	get rowSelection(): RowSelectionOptions | undefined {
		return this._rowSelection;
	}
	private _rowSelection: RowSelectionOptions | undefined;
	private _rawRowSelection: RowSelectionOptions | 'multiRow' | 'singleRow' | 'none';

	private updateRowSelectionOptions() {
		const value = this._rawRowSelection;
		if (typeof value === 'string') {
			switch (value) {
				case 'multiRow':
					this._rowSelection = {
						mode: 'multiRow',
						checkboxes: true,
						enableClickSelection: false,
						headerCheckbox: true,
						selectAll: 'filtered',
						hideDisabledCheckboxes: true,
						isRowSelectable: (params: any) => this.isRowSelectable(params.node || params),
					};
					break;
				case 'singleRow':
					this._rowSelection = {
						mode: 'singleRow',
						checkboxes: true,
						enableClickSelection: false,
						hideDisabledCheckboxes: true,
						isRowSelectable: (params: any) => this.isRowSelectable(params.node || params),
					};
					break;
				case 'none':
				default:
					this._rowSelection = undefined;
					break;
			}
		} else if (value) {
			this._rowSelection = {
				...value,
				isRowSelectable: value.isRowSelectable || ((params: any) => this.isRowSelectable(params.node || params)),
			};
		} else {
			this._rowSelection = undefined;
		}
	}

	@Input() editType: 'fullRow' | undefined;
	@Input() actions: Array<Action> = [];
	@Input() simple: boolean = false;
	@Input() triggerTable: () => void;

	@Input() actionButtonTxt: string;
	@Input() secondButtonTxt: string;
	@Input() actionButtonClass = 'secondary';
	@Input() secondButtonClass = 'danger';
	@Input() requiredPermission?: string | string[];
	@Input() actionButtonPermission?: string | string[];
	@Input() secondButtonPermission?: string | string[];
	@Input() csvPermission?: string | string[];
	@Input() updateOnSelect = false;
	@Input() selectedIds: Array<any> = [];
	@Input() set isRowSelectable(value: (node: RowNode) => boolean) {
		this._isRowSelectable = value;
		this.updateRowSelectionOptions();
	}
	get isRowSelectable(): (node: RowNode) => boolean {
		return this._isRowSelectable;
	}
	private _isRowSelectable: (node: RowNode) => boolean = () => true;
	@Input() actionButtonDisabled: boolean;
	@Input() secondButtonDisabled: boolean;
	@Input() onFirstDataRendered: any = () => {};
	@Input() onRowDataUpdated: any = () => {};
	@Input() onCellClicked: any = () => {};
	@Input() exportOffset: number = 1;

	@Output() rowsSelected = new EventEmitter<Array<IRowNode>>();
	@Output() rowEdited = new EventEmitter<Array<RowNode>>();
	@Output() actionButtonClick = new EventEmitter();
	@Output() secondButtonActionClick = new EventEmitter();
	@Output() pageChanged = new EventEmitter<any>();
	@Output() searchChanged = new EventEmitter<string>();

	@Input() serverSidePagination: boolean = false;
	@Input() totalRecords: number = 0;
	@Input() first: number = 0;

	// Status filter inputs/outputs
	@Input() filterOptions: Array<{ value: string; label: string }> = [];
	@Input() filterValue: string = '';
	@Input() filterPlaceholder: string = 'common_filter_placeholder_status';
	@Output() filterChanged = new EventEmitter<string>();

	@Input() headerHeight?: number;
	@Input() filterOptions2: Array<{ value: string; label: string; disabled?: boolean }> = [];
	@Input() filterValue2: string = '';
	@Input() filterPlaceholder2: string = 'multitenant_filter_placeholder';
	@Output() filterChanged2 = new EventEmitter<string>();

	onFilterSelectChanged2() {
		this.filterChanged2.emit(this.filterValue2);
	}

	onFilterSelectChanged() {
		this.saveTableState({ selectedFilterValue: this.filterValue });
		this.filterChanged.emit(this.filterValue);
	}

	pageSize: number = 30;
	pageSizeOptions: number[] = [30, 100, 300, 500];

	private gridApi!: GridApi;
	/** Ultimo domLayout applicato, per forzare un relayout solo quando la modalità cambia davvero. */
	private lastDomLayout: 'normal' | 'autoHeight' | null = null;
	private resizeObserver: ResizeObserver | null = null;

	tableLanguage = getAgGridLocale();
	showGrid = true;
	private isMobileView: boolean = false;
	private lastEmittedParams: string | null = null;

	gridOptions: GridOptions = {
		suppressMenuHide: true,
		animateRows: true,
		enableCellTextSelection: true,
		defaultColDef: {
			sortable: true,
			resizable: true,
			filter: undefined,
			flex: 1,
			wrapText: true,
			autoHeight: true,
		},
		isFullWidthRow: params => {
			// Check if mobile view (e.g. < 992px)
			return this.isMobileView;
		},
		getRowHeight: params => {
			if (this.isMobileView) {
				// Estimate: 20px padding + (visible cols * 50px) + 60px margin
				let visibleCols = 5;
				if (params.api) {
					const columns = params.api.getColumns();
					if (columns) {
						visibleCols = columns.filter(c => c.isVisible()).length;
					}
				} else if (this.columnDefs) {
					visibleCols = this.columnDefs.filter(c => !c.hide).length;
				}

				const selectionHeight = this.rowSelection ? 50 : 0;
				const calculatedHeight = 40 + visibleCols * 50 + 60 + selectionHeight;
				return Math.max(calculatedHeight, 300);
			}
			return null; // Default height for desktop
		},
		embedFullWidthRows: true,
		fullWidthCellRenderer: MobileRendererComponent,
		context: {
			componentParent: this,
		},
	};

	constructor(
		public _translateService: TranslateService,
		private elementRef: ElementRef,
		private _authService: AuthService,
		private _router: Router,
		private _tableStateService: TableStateService
	) {}

	get canViewTable(): boolean {
		if (this.requiredPermission) {
			return this.hasPermission(this.requiredPermission);
		}

		const inferredPermission = this.inferViewPermissionFromRoute();
		if (!inferredPermission) {
			return true; // No explicit permission required and couldn't infer from route
		}

		return this.hasPermission(inferredPermission);
	}

	get canShowActionButton(): boolean {
		return this.hasPermission(this.actionButtonPermission);
	}

	get canShowSecondButton(): boolean {
		return this.hasPermission(this.secondButtonPermission);
	}

	get canShowCsv(): boolean {
		return this.hasPermission(this.csvPermission);
	}

	get visibleActions(): Array<Action> {
		return (this.actions || []).filter(action => {
			if (action.permission) {
				return this.hasPermission(action.permission);
			}

			if (action.icon?.name === 'trash') {
				const inferred = this.inferActionPermissionFromRoute('delete');
				if (inferred) {
					return this._authService.hasPermission(inferred);
				}
			}

			return true;
		});
	}

	private hasPermission(permission?: string | string[]): boolean {
		if (!permission) {
			return true;
		}

		const permissions = Array.isArray(permission) ? permission : [permission];
		return this._authService.hasAnyPermission(permissions);
	}

	private inferViewPermissionFromRoute(): string | undefined {
		const url = this.normalizeUrl(this._router.url);
		if (!url) {
			return undefined;
		}

		const allMenus: CoreMenu[] = [...menu, ...universityMenu, ...schoolMenu, ...frontendMenu];

		const matched = this.findMenuByUrl(allMenus, url);
		const permissions = matched?.permissions || [];
		return permissions.length > 0 ? permissions[0] : undefined;
	}

	private inferActionPermissionFromRoute(action: string): string | undefined {
		const viewPerm = this.inferViewPermissionFromRoute();
		if (!viewPerm) return undefined;
		const base = viewPerm.endsWith('.view') ? viewPerm.replace(/\.view$/, '') : viewPerm;
		return `${base}.${action}`;
	}

	private findMenuByUrl(items: CoreMenu[], currentUrl: string): CoreMenu | undefined {
		for (const item of items || []) {
			const itemUrl = this.normalizeUrl(item.url);
			if (itemUrl && (itemUrl === currentUrl || currentUrl.startsWith(itemUrl + '/'))) {
				return item;
			}
			if (item.children?.length) {
				const found = this.findMenuByUrl(item.children as CoreMenu[], currentUrl);
				if (found) {
					return found;
				}
			}
		}
		return undefined;
	}

	private normalizeUrl(url?: string): string {
		if (!url) {
			return '';
		}
		return url.replace(/^\/+/, '').split('?')[0].split('#')[0];
	}

	filterId = 'filter-text-box-' + Math.random().toString(36).substring(7);

	// Lifecycle Hooks
	// -----------------------------------------------------------------------------------------------------

	/**
	 * On init
	 */
	ngOnInit() {
		if (this.tableName) {
			this.filterId = 'filter-text-box-' + this.tableName;
		}

		this.isMobileView = window.innerWidth < 992;
		this.gridOptions.defaultColDef = this.gridOptions.defaultColDef || {};
		this.gridOptions.defaultColDef.filter = !this.simple && !this.serverSidePagination;

		if (this.serverSidePagination) {
			this.gridOptions.defaultColDef.comparator = () => 0;
		}

		this.setTableLanguage();

		// Restituzione dello stato salvato (filtri a tendina, dimensione e indice di paginazione)
		const savedState = this._tableStateService.getState(this.tableName);
		if (savedState) {
			if (savedState.filterValue !== undefined) {
				this.filterValue = savedState.filterValue;
			}
			if (savedState.filterValue2 !== undefined) {
				this.filterValue2 = savedState.filterValue2;
			}
			if (savedState.rows) {
				this.pageSize = savedState.rows;
			}
			if (savedState.first !== undefined) {
				this.first = savedState.first;
			}
		}

		// Aggiorna la lingua quando cambia
		this._translateService.onLangChange.subscribe(res => {
			this.updateGrid();
		});
	}

	/**
	 * After view init - Imposta l'observer per il ridimensionamento automatico
	 */
	ngAfterViewInit() {
		// Osserva quando il componente diventa visibile o cambia dimensione
		this.resizeObserver = new ResizeObserver(entries => {
			for (const entry of entries) {
				if (entry.contentRect.width > 0 && this.gridApi) {
					const newIsMobileView = window.innerWidth < 992;
					if (this.isMobileView !== newIsMobileView) {
						this.isMobileView = newIsMobileView;
						setTimeout(() => {
							this.gridApi.setGridOption('rowData', this.rowData);
						}, 200);
					} else {
						this.safeSizeColumnsToFit();
					}
				}
			}
		});

		// Osserva l'elemento host del componente
		this.resizeObserver.observe(this.elementRef.nativeElement);
	}

	/**
	 * On destroy - Pulisce l'observer
	 */
	ngOnDestroy() {
		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
			this.resizeObserver = null;
		}
	}

	@HostListener('window:resize', ['$event'])
	onResize(event: Event) {
		if (this.gridApi) {
			const newIsMobileView = window.innerWidth < 992;

			if (this.isMobileView !== newIsMobileView) {
				this.isMobileView = newIsMobileView;
				// Mode changed: Force aggressive refresh to ensure correct layout
				setTimeout(() => {
					this.gridApi.setGridOption('rowData', this.rowData);
				}, 200);
			} else {
				// Just resizing within same mode
				this.safeSizeColumnsToFit();
			}
		}
	}

	public toggleAllOptions(selectAll?: boolean): void {
		if (!this.gridApi) return;

		if (selectAll !== undefined) {
			this.isAllSelected = selectAll;
		} else {
			this.isAllSelected = !this.isAllSelected;
		}

		if (this.isAllSelected) {
			this.gridApi.selectAll();
		} else {
			this.gridApi.deselectAll();
		}

		// isAllSelected will be updated by selectionChanged event which calls emitRows
		// this.rowsSelected.emit(this.gridApi.getSelectedNodes()); // This is already called by selectionChanged

		if (this.updateOnSelect) {
			this.updateRows();
		}
	}

	showLoadingOverlay() {
		this.gridApi.showLoadingOverlay();
	}

	showNoResultOverlay() {
		this.gridApi.showNoRowsOverlay();
	}

	hideOverlay() {
		this.gridApi.hideOverlay();
	}

	updateGrid() {
		this.showGrid = false;
		this.lastEmittedParams = null;
		this.gridApi = undefined as any;
		setTimeout(() => {
			this.tableLanguage = getAgGridLocale(this._translateService.currentLang);
			this.showGrid = true;
		}, 100);
	}

	private getStorageKey(): string {
		const universityId = sessionStorage.getItem('university') || 'global';
		const schoolId = sessionStorage.getItem('school') || 'global';
		return `tableState_${universityId}_${schoolId}_${this.tableName}`;
	}

	setTableLanguage() {
		this.tableLanguage = getAgGridLocale(this._translateService.currentLang);
	}

	onGridReady(params: GridReadyEvent) {
		this.gridApi = params.api;

		const savedState = this._tableStateService.getState(this.tableName);

		if (savedState) {
			if (savedState.columnFilters && this.parentPage && (this.parentPage as any).columnFilters) {
				(this.parentPage as any).columnFilters = { ...savedState.columnFilters };
			}
			if (savedState.filterValue !== undefined) {
				this.filterValue = savedState.filterValue;
				this.filterChanged.emit(this.filterValue);
			}
			if (savedState.filterValue2 !== undefined) {
				this.filterValue2 = savedState.filterValue2;
				this.filterChanged2.emit(this.filterValue2);
			}
		}

		// Restore quickFilter text into search input field & AG Grid
		if (savedState && savedState.quickFilter) {
			const quickFilter = savedState.quickFilter;
			setTimeout(() => {
				const input = document.getElementById(this.filterId) as HTMLInputElement;
				if (input) {
					input.value = quickFilter;
				}
				if (!this.serverSidePagination && this.gridApi && !this.gridApi.isDestroyed()) {
					this.gridApi.setGridOption('quickFilterText', quickFilter);
				} else if (this.serverSidePagination) {
					this.searchChanged.emit(quickFilter);
				}
			});
		}

		// Restore AG Grid column filter model (anche per server-side: serve solo a sincronizzare
		// lo stato "attivo" mostrato dall'header, il comparator server-side è no-op)
		if (savedState && savedState.filterModel) {
			try {
				this.gridApi.setFilterModel(savedState.filterModel);
			} catch (e) {
				console.debug('Error restoring filterModel', e);
			}
		}

		// Restore sort state if present
		if (savedState && savedState.sortBy) {
			try {
				this.gridApi.applyColumnState({
					state: [{ colId: savedState.sortBy, sort: savedState.sortDir || 'asc' }],
					defaultState: { sort: null },
				});
			} catch (e) {
				console.debug('Error restoring sort state', e);
			}
		}

		// Emit initial state always for serverSidePagination to avoid race conditions with ngOnInit
		if (this.serverSidePagination) {
			setTimeout(() => {
				const rows = savedState?.rows || this.pageSize || 30;
				const first = savedState?.first !== undefined ? savedState.first : this.first || 0;
				const currentSort = this.getCurrentSortState();
				const search = savedState?.quickFilter || '';
				this.emitPageChanged({
					first,
					rows,
					page: Math.floor(first / rows),
					search,
					sortBy: currentSort.sortBy || savedState?.sortBy || null,
					sortDir: currentSort.sortDir || savedState?.sortDir || null,
				});
			});
		}

		// Ritenta il ridimensionamento dopo che il DOM è completamente renderizzato
		setTimeout(() => {
			this.safeSizeColumnsToFit();
		}, 100);
	}

	onGridSizeChanged() {
		if (this.gridApi) {
			// Verifica che la griglia sia visibile e abbia una larghezza valida
			const gridElement = document.querySelector(`ag-grid-angular[class*="ag-theme"]`);
			if (gridElement && gridElement.clientWidth > 0) {
				this.gridApi.sizeColumnsToFit();
			}
			this.gridApi.getSelectedNodes();
		}
	}

	/**
	 * Ridimensiona le colonne in modo sicuro, verificando prima che la griglia sia visibile
	 */
	private safeSizeColumnsToFit(): void {
		if (!this.gridApi) return;

		// Ottieni l'elemento della griglia
		const gridElement = document.querySelector(`ag-grid-angular[class*="ag-theme"]`) as HTMLElement;

		if (gridElement && gridElement.clientWidth > 0) {
			try {
				this.gridApi.sizeColumnsToFit();
			} catch (error) {
				// Se fallisce, non fare nulla - la griglia potrebbe non essere ancora pronta
				console.debug('sizeColumnsToFit non disponibile, griglia non ancora visibile');
			}
		}
	}

	/**
	 * Metodo pubblico per forzare il ridimensionamento delle colonne
	 * Utile quando la tabella diventa visibile (es. apertura accordion)
	 */
	public forceColumnResize(): void {
		setTimeout(() => {
			this.safeSizeColumnsToFit();
		}, 50);
	}

	onFilterTextBoxChanged() {
		const searchFormLabel = (document.getElementById(this.filterId) as HTMLInputElement).value;
		this.saveQuickFilter(searchFormLabel);

		if (!this.serverSidePagination) {
			this.gridApi.setGridOption('quickFilterText', searchFormLabel);
			if (!searchFormLabel) {
				this.saveFilterModel();
			}
		} else {
			this.searchChanged.emit(searchFormLabel);
		}
	}

	onBtnExport(offset: number = 0) {
		if (!this.gridApi) return;

		const allColumns = this.gridApi.getColumns() || [];
		if (offset >= allColumns.length) {
			console.warn('Offset troppo alto');
			return;
		}

		const columnKeys = allColumns.slice(offset).map(col => col.getColId());

		this.gridApi.exportDataAsCsv({
			columnKeys,
			columnSeparator: ';',
		});
	}

	onPageSizeChanged() {
		this.pageSize = Number(this.pageSize) || 30;
		this.first = 0;
		this.saveTableState({ rows: this.pageSize, first: this.first });

		if (this.gridApi) {
			if (!this.serverSidePagination) {
				this.gridApi.setGridOption('paginationPageSize', this.pageSize);
			} else {
				const sortState = this.getCurrentSortState();
				this.emitPageChanged({
					first: 0,
					rows: this.pageSize,
					page: 0,
					sortBy: sortState.sortBy,
					sortDir: sortState.sortDir,
				});
			}
		}
	}

	/** Pagina scelta in ngb-pagination (1-based): stesso evento che dava p-paginator ({ first, rows, page, pageCount }). */
	onServerPagePick(page: number) {
		const first = (page - 1) * this.pageSize;
		if (first === this.first) return;
		this.onServerPageChange({ first, rows: this.pageSize, page: page - 1, pageCount: Math.ceil(this.totalRecords / this.pageSize) });
	}

	min(a: number, b: number): number {
		return Math.min(a, b);
	}

	onServerPageChange(event: any) {
		this.first = event.first;
		this.pageSize = event.rows;
		this.saveTableState({ first: this.first, rows: this.pageSize });
		const sortState = this.getCurrentSortState();
		this.emitPageChanged({
			...event,
			sortBy: sortState.sortBy,
			sortDir: sortState.sortDir,
		});
	}

	onSortChanged() {
		const sortState = this.getCurrentSortState();
		this.saveTableState({ sortBy: sortState.sortBy, sortDir: sortState.sortDir });

		if (!this.serverSidePagination) {
			return;
		}

		this.first = 0;
		this.saveTableState({ first: this.first });

		const search = ((document.getElementById(this.filterId) as HTMLInputElement)?.value || '').toString();
		this.emitPageChanged({
			first: 0,
			rows: this.pageSize,
			page: 0,
			search,
			sortBy: sortState.sortBy,
			sortDir: sortState.sortDir,
		});
	}

	isAllSelected: boolean = false;

	emitRows($event: Array<IRowNode>) {
		if (this.gridApi) {
			const selectedCount = this.gridApi.getSelectedNodes().length;
			const totalCount = this.gridApi.getDisplayedRowCount();
			this.isAllSelected = totalCount > 0 && selectedCount === totalCount;
		}
		this.rowsSelected.emit(this.gridApi.getSelectedNodes());
		if (this.updateOnSelect) {
			this.updateRows();
		}
	}

	updateRows() {
		this.gridApi.applyTransaction({ update: this.rowData });
	}

	onRowDataUpdatedInternal($event: any) {
		const savedState = this._tableStateService.getState(this.tableName);
		const input = document.getElementById(this.filterId) as HTMLInputElement;
		const filterText = input?.value || savedState?.quickFilter || '';

		if (this.gridApi && !this.gridApi.isDestroyed() && !this.serverSidePagination) {
			if (filterText) {
				this.gridApi.setGridOption('quickFilterText', filterText);
			}
			if (savedState?.filterModel) {
				try {
					this.gridApi.setFilterModel(savedState.filterModel);
				} catch (e) {}
			}
		}

		// Quando il numero di righe fa cambiare modalità (autoHeight ⇄ normal), AG Grid può lasciare il
		// viewport non ricalcolato. Forziamo un relayout ri-applicando rowData al tick successivo (dopo che
		// i binding [domLayout]/[style.height] sono stati aggiornati). La guardia su lastDomLayout evita loop.
		const layout = this.effectiveDomLayout;
		if (layout !== this.lastDomLayout) {
			this.lastDomLayout = layout;
			setTimeout(() => {
				if (this.gridApi && !this.gridApi.isDestroyed()) {
					this.gridApi.setGridOption('rowData', this.rowData);
					if (!this.serverSidePagination) {
						if (filterText) {
							this.gridApi.setGridOption('quickFilterText', filterText);
						}
						if (savedState?.filterModel) {
							try {
								this.gridApi.setFilterModel(savedState.filterModel);
							} catch (e) {}
						}
					}
				}
			});
		}

		if (this.onRowDataUpdated) {
			this.onRowDataUpdated($event);
		}
	}

	emitEditedRow($event: RowValueChangedEvent) {
		this.rowEdited.emit($event.data);
	}

	onCellValueChanged(event: CellValueChangedEvent) {
		// console.log(event);
		// console.log(
		//   'onCellValueChanged: ' + event.colDef.field + ' = ' + event.newValue
		// );
	}

	actionButtonEvent($event: Event) {
		this.actionButtonClick.emit($event);
	}

	secondButtonEvent($event: Event) {
		this.secondButtonActionClick.emit($event);
	}

	saveFilterModel() {
		if (this.gridApi && !this.gridApi.isDestroyed()) {
			const filterModel = this.gridApi.getFilterModel();
			const input = document.getElementById(this.filterId) as HTMLInputElement;
			const quickFilter = input ? input.value : '';
			this.saveTableState({
				filterModel,
				quickFilter,
			});
		}
	}

	private saveQuickFilter(quickFilter: string): void {
		this.saveTableState({ quickFilter });
	}

	private getCurrentSortState(): { sortBy: string | null; sortDir: 'asc' | 'desc' | null } {
		if (!this.gridApi || this.gridApi.isDestroyed()) {
			return { sortBy: null, sortDir: null };
		}

		const sortedColumn = this.gridApi.getColumnState().find(col => col.sort === 'asc' || col.sort === 'desc');
		if (!sortedColumn) {
			return { sortBy: null, sortDir: null };
		}

		return {
			sortBy: sortedColumn.colId,
			sortDir: sortedColumn.sort as 'asc' | 'desc',
		};
	}

	private saveTableState(partialState: Record<string, any>): void {
		if (this.parentPage && (this.parentPage as any).columnFilters) {
			partialState = {
				columnFilters: (this.parentPage as any).columnFilters,
				...partialState,
			};
		}
		this._tableStateService.saveState(this.tableName, partialState);
	}

	// Mobile Sort Helpers
	get visibleColumns(): any[] {
		if (this.gridApi && !this.gridApi.isDestroyed() && this.gridApi.getColumns()) {
			const columns = this.gridApi.getColumns() || [];
			return columns.filter(
				c =>
					c.isVisible() &&
					c.getColDef().headerName &&
					c.getColDef().headerName !== 'Actions' &&
					c.getColDef().headerName !== 'Azioni' &&
					c.isSortable()
			);
		}
		return this.columnDefs
			? this.columnDefs.filter(c => !c.hide && c.headerName && c.headerName !== 'Actions' && c.headerName !== 'Azioni')
			: [];
	}

	onMobileSort(col: any) {
		if (!this.gridApi || this.gridApi.isDestroyed()) return;

		const colId = col.getColId ? col.getColId() : col.colId || col.field;
		const currentSort = this.gridApi.getColumnState().find(c => c.colId === colId);
		let newSortOrder: 'asc' | 'desc' | null = 'asc';

		if (currentSort && currentSort.sort === 'asc') {
			newSortOrder = 'desc';
		} else if (currentSort && currentSort.sort === 'desc') {
			newSortOrder = null;
		}

		this.gridApi.applyColumnState({
			state: [{ colId: colId, sort: newSortOrder }],
			defaultState: { sort: null },
		});
	}

	isSorted(col: any): boolean {
		if (!this.gridApi || this.gridApi.isDestroyed() || !this.gridApi.getColumnState()) return false;
		const colId = col.getColId ? col.getColId() : col.colId || col.field;
		const state = this.gridApi.getColumnState().find(c => c.colId === colId);
		return !!state && !!state.sort;
	}

	getSortIcon(col: any): any {
		if (!this.gridApi || this.gridApi.isDestroyed() || !this.gridApi.getColumnState()) return '';
		const colId = col.getColId ? col.getColId() : col.colId || col.field;
		const state = this.gridApi.getColumnState().find(c => c.colId === colId);
		return state?.sort === 'asc' ? 'sort-up' : 'sort-down';
	}

	private emitPageChanged(params: any): void {
		const serialized = JSON.stringify(params);
		if (this.lastEmittedParams === serialized) {
			return;
		}
		this.lastEmittedParams = serialized;
		this.pageChanged.emit(params);
	}
}
